import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import { useBlueconicContext } from '@contexts/BlueconicContext';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { article_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { captureBlueconicClickEvent } from '@utils/blueconic/utils';
import { UtmParams, articlePathWithParams } from '@utils/queryStringHelper';

import IconProTagThumbnail from '../Images/Rebranding/IconProTagThumbnail';
import ArticleAsset from './ArticleAsset';

interface ArticleImageProps {
  className?: string;
  id: number;
  isMobileFullWidth?: boolean;
  isPro: boolean;
  picture: string;
  sizes?: string;
  skipOptimization?: boolean;
  slug: string;
  target?: string;
  title: string;
  offerCode?: string;
  utmParams?: UtmParams;
  newsletterSlug?: string;
  posterPicture?: string;
}

const ArticleImage = ({
  className,
  id,
  isMobileFullWidth,
  isPro,
  picture,
  sizes,
  skipOptimization = false,
  slug,
  target,
  title,
  offerCode,
  utmParams,
  newsletterSlug,
  posterPicture,
}: ArticleImageProps) => {
  const { env } = useAppContext();
  const { trackEvent } = useEventTracking();
  const { variantId } = useBlueconicContext();
  const hydrated = useServerSideRendering();
  const [articlePath, setArticlePath] = useState(article_path(slug));

  useEffect(() => {
    if (!hydrated) return;
    setArticlePath(
      articlePathWithParams({
        slug: slug,
        offer: offerCode,
        utm: utmParams,
        newsletterSlug: newsletterSlug,
      })
    );
  }, [hydrated, slug, offerCode, utmParams, newsletterSlug]);

  const clickTargetCapture = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.ArticleImage,
      })
    );
    if (variantId) {
      captureBlueconicClickEvent(variantId);
    }
  };
  return (
    <a
      href={articlePath}
      onClickCapture={clickTargetCapture}
      className={clsx(
        'relative block flex-shrink-0',
        {
          'max-sm:h-[calc(100vw*9/16)]': !isMobileFullWidth,
          '-mx-16 h-[calc(100vw*9/16)] sm:mx-0': isMobileFullWidth,
        },
        className
      )}
      target={target}
    >
      {isPro && <IconProTagThumbnail className="absolute right-0 top-0" />}
      <ArticleAsset
        posterPicture={posterPicture}
        title={title}
        id={id}
        picture={picture}
        env={env}
        skipOptimization={skipOptimization}
        sizes={sizes}
      />
    </a>
  );
};

export default ArticleImage;
